
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface ProductPrice {
  id: string;
  barcode: string | null;
  name: string;
  current_price: number | null;
  category: string | null;
  category_name: string | null;
  supplier: string | null;
  last_synced: string;
  square_item_id: string;
  description: string | null;
  inventory_tracking: boolean | null;
  current_stock: number | null;
}

interface ProductCardProps {
  product: ProductPrice;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const isMobile = useIsMobile();

  return (
    <Card className="shadow-lg border-2 border-primary/20 bg-gradient-to-br from-background to-muted/30">
      <CardContent className="p-8">
        {/* Main Product Info */}
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold mb-4 text-foreground">{product.name}</h3>
          
          {/* Large Price Display */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl p-6 mb-6 shadow-lg">
            <div className="text-6xl font-bold mb-2">
              {product.current_price ? `$${product.current_price.toFixed(2)}` : 'No Price'}
            </div>
            <div className="text-xl font-medium opacity-90">Current Retail Price</div>
          </div>

          {/* Barcode Display */}
          {product.barcode && (
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <p className="text-xl font-mono font-bold tracking-wider">{product.barcode}</p>
              <p className="text-sm text-muted-foreground">Barcode</p>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg">
          {product.supplier && (
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <p className="font-semibold text-muted-foreground">Supplier</p>
              <p className="font-bold">{product.supplier}</p>
            </div>
          )}
          
          {product.inventory_tracking && (
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <p className="font-semibold text-muted-foreground">Stock</p>
              <p className="font-bold">{product.current_stock ?? 'Unknown'}</p>
            </div>
          )}
        </div>

        {/* Category Badge */}
        {(product.category_name || product.category) && (
          <div className="text-center mt-6">
            <Badge variant="secondary" className="text-lg px-4 py-2">
              {product.category_name || product.category}
            </Badge>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6 pt-4 border-t border-muted">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Package className="w-4 h-4" />
            <span className="text-sm">Square POS • ID: {product.square_item_id}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
